/**
 * useInstallPrompt — hook + shared config for the smart install banner.
 * ─────────────────────────────────────────────────────────────────────
 * Central place for:
 *   • Store URLs (kept in sync with utils/openInNativeApp.js)
 *   • Platform gating — bails out entirely for Despia native users, and
 *     for desktop users where "install the app" is meaningless.
 *   • Dismissal cooldown so we don't nag: 7-day gap after any explicit
 *     "Not now" tap, plus a soft cap of 3 impressions per browser
 *     session so the banner doesn't reappear over-and-over during one
 *     visit even before dismissal.
 *
 * This hook does NOT render anything — it returns a `{ visible, target,
 * platform, dismiss }` object that any renderer can consume. Keeping
 * logic here means the banner can be dropped onto multiple pages
 * without duplicating the state machine.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPlatformInfo } from '@/services/platformDetection';

// Kept in sync with /app/frontend/src/utils/openInNativeApp.js so a
// single change propagates to every store CTA in the app.
export const APP_STORE_URL =
  'https://apps.apple.com/us/app/eili-bible-study-tool/id6761586127';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.askeili.android';

/**
 * Append attribution parameters to the store URL so App Store Connect
 * (via `ct=`) and Google Play (via the Install Referrer API) can
 * measure how many installs originated from each web surface.
 *
 * Format:
 *   • App Store — `?ct=<campaign>` where campaign is
 *     `webinstall-<source>` (App Store Connect surfaces this as the
 *     "Campaign" dimension in Sources reports).
 *   • Google Play — `?referrer=utm_source%3Dweb%26utm_medium%3D
 *     install-banner%26utm_campaign%3D<source>` (Play Console surfaces
 *     this under Acquisition Reports → UTM).
 *
 * `source` should be a stable, kebab-case string identifying the
 * placement: `landing-hero`, `home-badges`, `reflection-banner`,
 * `community-banner`, etc. Unknown sources fall back to `generic`.
 */
export function buildStoreUrl(baseUrl, store, source = 'generic') {
  const safeSource = String(source || 'generic').replace(/[^a-z0-9\-_.]/gi, '');
  try {
    const url = new URL(baseUrl);
    if (store === 'app-store') {
      // Preserve any existing query params, then add ct= for App
      // Store Connect campaign attribution.
      url.searchParams.set('ct', `webinstall-${safeSource}`);
      // pt (provider token) is optional; leaving it off is fine —
      // ct alone is enough for campaign attribution.
    } else if (store === 'play-store') {
      // Google Play expects a single `referrer` param whose value is
      // a url-encoded query string. Build it explicitly so any prior
      // referrer isn't clobbered by shorter values.
      const inner = new URLSearchParams({
        utm_source: 'web',
        utm_medium: 'install-banner',
        utm_campaign: safeSource,
      }).toString();
      url.searchParams.set('referrer', inner);
    }
    return url.toString();
  } catch {
    // Shouldn't happen — but if URL parsing ever fails on some old
    // WebView, degrade gracefully to the raw store URL rather than
    // 404'ing.
    return baseUrl;
  }
}

const STORAGE_KEY_DISMISSED_AT = 'eili.install_banner.dismissed_at';
const STORAGE_KEY_SESSION_SHOWN = 'eili.install_banner.session_shown';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_PER_SESSION = 3;

/**
 * Detect what "target" (if any) makes sense for the current visitor.
 * Returns `null` when we should NEVER show the install banner:
 *   • Despia native wrapper (already using the app)
 *   • Desktop / laptop (can't install a mobile app)
 * Returns `{ store: 'app-store' | 'play-store', url, platformLabel }`
 * for iOS Safari + Android Chrome web visitors.
 */
export function detectInstallTarget() {
  if (typeof window === 'undefined') return null;
  const info = getPlatformInfo();
  // Rule 1: user is already inside the native app — hide everything.
  if (info.isDespia) return null;
  // Rule 1b: installed PWA (iOS Add-to-Home-Screen OR Android/Desktop
  // installed PWA) — the user is already inside our "app" shell, so
  // suggesting they install it again is confusing.
  const isIOSStandalone = window.navigator && window.navigator.standalone === true;
  const isDisplayStandalone = typeof window.matchMedia === 'function'
    && (() => { try { return window.matchMedia('(display-mode: standalone)').matches; } catch { return false; } })();
  if (isIOSStandalone || isDisplayStandalone) return null;
  const ua = (navigator.userAgent || '').toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) && !info.isDespia;
  const isAndroid = /android/.test(ua) && !info.isDespia;
  if (!isIOS && !isAndroid) return null;
  // Rule 2: only mobile web contexts get a store CTA. TWA / Android
  // WebView cases where the user is *already* using an installed
  // wrapper are excluded here — they'd be caught by `isDespia`.
  return isIOS
    ? { store: 'app-store', url: APP_STORE_URL, platformLabel: 'iPhone' }
    : { store: 'play-store', url: PLAY_STORE_URL, platformLabel: 'Android' };
}

/**
 * React hook that decides whether the smart install banner should be
 * visible for the current visitor. Consumers use `visible`, `target`,
 * and `dismiss` to render.
 *
 * Options:
 *   • `respectCooldown` — set to false to force the banner regardless
 *     of dismissal state (used by the always-visible Home page badges
 *     row so it can stay put; the periodic banner keeps this true).
 *   • `sessionCap` — override the per-session impression cap if a
 *     given surface should honour a stricter or looser limit.
 */
export default function useInstallPrompt({ respectCooldown = true, sessionCap = MAX_PER_SESSION } = {}) {
  const target = useMemo(() => detectInstallTarget(), []);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!target) return; // never show — desktop, or already in native
    if (!respectCooldown) {
      setVisible(true);
      return;
    }
    // Cooldown check
    try {
      const dismissedAt = Number(localStorage.getItem(STORAGE_KEY_DISMISSED_AT) || 0);
      if (dismissedAt && Date.now() - dismissedAt < COOLDOWN_MS) return;
    } catch { /* private mode — treat as never dismissed */ }
    // Session-cap check
    try {
      const shownStr = sessionStorage.getItem(STORAGE_KEY_SESSION_SHOWN) || '0';
      const shown = Number(shownStr);
      if (shown >= sessionCap) return;
      sessionStorage.setItem(STORAGE_KEY_SESSION_SHOWN, String(shown + 1));
    } catch { /* private mode — always show */ }
    setVisible(true);
  }, [target, respectCooldown, sessionCap]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY_DISMISSED_AT, String(Date.now()));
    } catch { /* private mode — best-effort */ }
    setVisible(false);
  }, []);

  return { visible, target, dismiss };
}
